import { useStore } from 'vuex';
import { ref, computed, watch } from 'vue';
import { useType as useOrientation } from '@modules/types';
import { useMutation } from '@composables/utils/useMutation';
import { useBrand } from '@composables/useBrand';
import { AddToListService } from '@composables/useAddToList/services/AddToList.service';
import useFetch from '@composables/api/useFetch';
import { useTraits } from '@composables/useTraits';
import { useColor } from '@modules/color/composables';
import { useSize } from '@modules/size/composables';
import { useUpc, validateUPC } from '@modules/upc';
import useAvailability from '@modules/upc/composables/useAvailability';
import { getUserIds, setUserCookies } from '@utils/cookiesUtils';
import { fireLinkTag } from '../../features/analytics/tagManager';

/**
 * * Composable that handles the Add To List functionality from PDP level
 * @param {ProductId} productId
 * @param {Object} [options] - The options object.
 * @param {((result: any) => void)} [options.onSuccess] - The callback function that will be called on success.
 * @param {((result: Error) => void)} [options.onError] - The callback function that will be called on error.
 * @param {(customerList: AllCustomerListsResponse.CustomerList ) => void} [options.slideoutOnSuccess]
 * @param {FeatureEligibleFlags} [featureEligibility] - The feature eligibility object.
 */
export function useAddToList(productId, {
  onSuccess, onError, slideoutOnSuccess,
} = {}, featureEligibility) {
  const store = useStore();
  const { selectedSize, selectedColor } = useTraits(productId);
  const { types: orientationArray, selectedType: selectedOrientation, label: typeName } = useOrientation(productId);

  const listName = ref('');
  const listId = ref('');

  const {
    selectedUpcId,
  } = useUpc(productId);
  const { isSizeAvailable, isColorAvailable } = useAvailability(productId);
  const { colors } = useColor(productId);
  const { sizes, label: sizeName } = useSize(productId);

  const { isMcom } = useBrand();

  const addToListService = new AddToListService(isMcom);

  const closeSlideout = () => store.commit('slideoutStore/CLOSE', `add-to-list-${productId}`);
  const { isMattress3v } = featureEligibility || {};

  const validate = () => validateUPC({
    selectedColor: selectedColor.value,
    selectedSize: selectedSize.value,
    selectedOrientation: selectedOrientation.value,
    colors: colors.value,
    sizes: sizes.value,
    orientations: orientationArray.value,
    isColorAvailable,
    isSizeAvailable,
    typeName: typeName.value,
    sizeName: sizeName?.value,
    excludeColorLabel: isMattress3v,
  });

  /**
   * @param {AllCustomerListsResponse} response
   */
  async function handleListsSuccess(response) {
    onSuccess?.(response);

    if (Array.isArray(response.CustomerList.list) && response.CustomerList.list.length > 1) {
      slideoutOnSuccess && slideoutOnSuccess(response.CustomerList); // eslint-disable-line no-unused-expressions
    } else {
      await addToListMutation.mutate(); // eslint-disable-line no-use-before-define
    }
  }

  /**
   * @param {CustomerListResponse} response
   */
  function handleDefaultListSuccess(response) {
    /*
    * Set cookies for user.
    * If it is guest user, we will get from response userGuid and userId.
    * It will give a possibility for guest user to go to wishlist and see all saved items
    * */
    setUserCookies({
      guid: response.CustomerList.user.guid,
      id: response.CustomerList.user.id.toString(),
    });

    const [list] = response.CustomerList.list;
    listName.value = list.name;
    listId.value = list.listGuid;

    if (response.CustomerList.meta) {
      const { data } = response.CustomerList.meta.analytics;
      fireLinkTag(data);
    }
  }

  const addToItemList = () => addToListService.addToList({
    productId,
    selectedUpcId: selectedUpcId.value,
    validationMessage: validate(),
  });

  const addToListMutation = useMutation(addToItemList, {
    onSuccess: handleDefaultListSuccess,
  });

  const addToCustomListMutation = useMutation(addToListService.addToCustomList, {
    onSuccess: handleDefaultListSuccess,
    onError,
  });

  const getAllLists = useFetch(addToListService.getAllLists);

  function resetState() {
    listName.value = '';
    listId.value = '';
    addToListMutation.resetError();
    addToCustomListMutation.resetError();
    getAllLists.resetError();
  }

  async function addToList() {
    resetState();
    const { isSignedIn } = getUserIds();

    if (isSignedIn && selectedUpcId.value) {
      await getAllLists.fetch();
      return;
    }
    await addToListMutation.mutate();
  }

  /**
   * @param {string} listGuid
   */
  async function addToCustomList(listGuid) {
    await addToCustomListMutation.mutate({
      listGuid,
      selectedUpcId: selectedUpcId.value,
    });
  }

  const isLoading = computed(
    () => addToListMutation.isLoading.value || getAllLists.isLoading.value,
  );

  const error = computed(() => addToListMutation.error.value || getAllLists.error.value || addToCustomListMutation.error.value);

  watch([selectedColor, selectedSize], resetState);
  watch(getAllLists.result, handleListsSuccess);

  return {
    closeSlideout,
    addToList,
    addToCustomList,
    isLoading,
    state: {
      listName,
      listId,
    },
    error,
    listsData: getAllLists.result,
  };
}

export default useAddToList;
